import React, { useState, useRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Chip, styled } from '@mui/material';
import { Snippet } from './Interface';

const StyledChip = styled(Chip)(({ theme }) => ({
  '& .MuiChip-icon': { color: theme.palette.secondary.dark, height: '20px', width: '20px', margin: '0px' },
  '& .MuiChip-label': {
    color: theme.palette.secondary.dark,
    padding: '0px 6px',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '&.MuiChip-filled, &.MuiChip-filled.MuiChip-clickable': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.secondary.dark,
    borderColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.secondary.dark,
      borderColor: theme.palette.secondary.main,
      borderWidth: '1px',
      borderStyle: 'solid',
    },
  },
  '&.MuiChip-outlined, &.MuiChip-outlined.MuiChip-clickable': {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: 'default',
    },
  },
  '&.Mui-disabled': {
    opacity: 1,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
    borderStyle: 'solid',
    '& .MuiChip-label': {
      color: theme.palette.primary.main,
    },
    '& .MuiChip-icon': {
      color: theme.palette.primary.main,
    },
  },
  height: '30px',
  padding: '3px 4px',
  maxWidth: '100%',
}));

interface RelatedChipProps {
  label: string;
  icon?: React.ReactElement;
  status?: string;
  selected?: boolean;
  onClick?: () => void;
  snippets?: Snippet[];
  variant?: 'filled' | 'outlined';
  size?: 'small' | 'medium';
  isMissingDocument?: boolean;
}

const RelatedChip: React.FC<RelatedChipProps> = ({
  label,
  icon,
  status,
  selected = false,
  onClick,
  snippets = [],
  variant = 'outlined',
  size = 'small',
  isMissingDocument = false,
}) => {
  const [open, setOpen] = useState(false);
  const [currentSnippetIndex, setCurrentSnippetIndex] = useState(0);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handlePrev = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setCurrentSnippetIndex((idx) => Math.max(0, idx - 1));
  };
  const handleNext = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setCurrentSnippetIndex((idx) => Math.min(snippets.length - 1, idx + 1));
  };

  // Keyboard navigation
  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, snippets.length]);

  React.useEffect(() => {
    if (currentSnippetIndex > snippets.length - 1) {
      setCurrentSnippetIndex(0);
    }
  }, [snippets.length]);

  return (
    <Box
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      sx={{
        display: 'inline-block',
        position: 'relative',
        padding: '8px',
        margin: '-8px', // Compensate for padding to maintain layout
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          height: '16px', // Extend the safe area below the chip
          pointerEvents: 'auto',
        },
      }}>
      <Tooltip title={isMissingDocument ? 'This is mentioned elsewhere but cannot be found in the system' : ''}>
        <Box ref={anchorRef} component="div" sx={{ display: 'inline-block' }}>
          <StyledChip
            size={size}
            label={label}
            icon={icon}
            onClick={!isMissingDocument ? onClick : undefined}
            variant={selected ? 'filled' : variant}
            data-testid={`chip-with-popper-${label}`}
            disabled={isMissingDocument}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default RelatedChip;
